import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { professionalsApi } from "../../../services/professionalsApi";
import type { Professional } from "../../../services/professionalsApi";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  ViewMode,
  ErrorState,
  Loading,
  SearchBar,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { Icons } from "../../shared/Icons";
import { CreateProfessionalModal } from "./CreateProfessionalModal";
import { ProfessionalsGridView } from "./ProfessionalsGridView";
import { ProfessionalsListView } from "./ProfessionalsListView";
import { ResetProfessionalPasswordModal } from "./ResetProfessionalPasswordModal";
import { viewModeStorage } from "../../../utils/viewModeStorage";

export const AdminProfessionals: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : null;

  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProfessional, setEditingProfessional] =
    useState<Professional | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [professionalToDelete, setProfessionalToDelete] = useState<
    number | null
  >(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [professionalToResetPassword, setProfessionalToResetPassword] =
    useState<Professional | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    viewModeStorage.load("professionals")
  );
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (company?.id) {
      fetchProfessionals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, isProfessional, loggedInProfessionalId]);

  const fetchProfessionals = async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);

    try {
      let response;
      // If user is a professional, fetch only their own data
      if (isProfessional && loggedInProfessionalId) {
        response = await professionalsApi.getProfessionalById(loggedInProfessionalId);
        if (response.success && response.data) {
          setProfessionals([response.data]);
        } else {
          setError(response.error || "Falha ao carregar profissional");
        }
      } else {
        // If user is company admin, fetch all professionals
        response = await professionalsApi.getProfessionalsByCompany();
        if (response.success && response.data) {
          setProfessionals(response.data);
        } else {
          setError(response.error || "Falha ao carregar profissionais");
        }
      }
    } catch (err) {
      setError("Falha ao carregar profissionais");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (professional: Professional) => {
    setEditingProfessional(professional);
    setShowCreateForm(true);
  };

  const handleDelete = (id: number) => {
    setProfessionalToDelete(id);
    setShowDeleteModal(true);
  };

  const handleManageAvailability = (professional: Professional) => {
    navigate(`/admin/professionals/${professional.id}/availability`);
  };

  const handleResetPassword = (professional: Professional) => {
    setProfessionalToResetPassword(professional);
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordClose = () => {
    setShowResetPasswordModal(false);
    setProfessionalToResetPassword(null);
  };

  const handleViewModeChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
    viewModeStorage.save("professionals", newViewMode);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Filter professionals based on search query (only if not professional user)
  const filteredProfessionals = isProfessional
    ? professionals // For professional users, show all (which is just one)
    : professionals.filter(
        (professional) =>
          professional.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          professional.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          professional.phoneNumber.includes(searchQuery)
      );

  const confirmDelete = async () => {
    if (!professionalToDelete) return;

    setDeleteLoading(true);
    setError(null);

    try {
      const response = await professionalsApi.deleteProfessional(
        professionalToDelete
      );
      if (response.success) {
        await fetchProfessionals();
        setShowDeleteModal(false);
        setProfessionalToDelete(null);
      } else {
        setError(response.error || "Falha ao excluir profissional");
      }
    } catch (err) {
      setError("Falha ao excluir profissional");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProfessionalToDelete(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingProfessional(null);
  };

  const handleProfessionalSaved = () => {
    fetchProfessionals();
    setShowCreateForm(false);
    setEditingProfessional(null);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Group sx={{ fontSize: 28 }} />}
        title={isProfessional ? "Meu Perfil" : "Gerenciamento de Profissionais"}
        subtitle={
          isProfessional
            ? "Visualize e edite suas informações"
            : "Gerencie a equipe de profissionais"
        }
        buttonText={isProfessional ? undefined : "Novo Profissional"}
        onButtonClick={isProfessional ? undefined : () => setShowCreateForm(true)}
        buttonDisabled={loading}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        showViewToggle={!isProfessional}
        midleContent={
          !isProfessional ? (
            <SearchBar
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Buscar por nome, email ou telefone..."
              className="w-64"
            />
          ) : undefined
        }
      />

      {/* Error Message */}
      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => window.location.reload()}
        />
      )}

      <CreateProfessionalModal
        isOpen={showCreateForm}
        onClose={handleCancel}
        onSuccess={handleProfessionalSaved}
        editingProfessional={editingProfessional}
      />

      <ResetProfessionalPasswordModal
        isOpen={showResetPasswordModal}
        onClose={handleResetPasswordClose}
        professional={professionalToResetPassword}
      />

      {/* Professionals Content */}
      {loading && !showCreateForm ? (
        <Loading
          message="Carregando profissionais..."
          size="xl"
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-12"
        />
      ) : (
        <Card className="p-8">
          {filteredProfessionals.length === 0 ? (
            <EmptyState
              icon={<Icons.Group sx={{ fontSize: 80 }} />}
              title={
                searchQuery
                  ? "Nenhum profissional encontrado"
                  : "Nenhum profissional encontrado"
              }
              description={
                searchQuery
                  ? "Tente buscar com outros termos"
                  : "Adicione seu primeiro profissional para começar!"
              }
              actionText={
                !searchQuery ? "Adicionar Primeiro Profissional" : undefined
              }
              onAction={
                !searchQuery ? () => setShowCreateForm(true) : undefined
              }
            />
          ) : (
            <>
              {viewMode === "grid" ? (
                <ProfessionalsGridView
                  professionals={filteredProfessionals}
                  onEdit={handleEdit}
                  onDelete={isProfessional ? undefined : handleDelete}
                  onManageAvailability={handleManageAvailability}
                  onResetPassword={handleResetPassword}
                  loading={loading}
                  companyCategory={company?.category}
                  isProfessional={isProfessional}
                />
              ) : (
                <ProfessionalsListView
                  professionals={filteredProfessionals}
                  onEdit={handleEdit}
                  onDelete={isProfessional ? undefined : handleDelete}
                  onManageAvailability={handleManageAvailability}
                  onResetPassword={handleResetPassword}
                  loading={loading}
                  companyCategory={company?.category}
                  isProfessional={isProfessional}
                />
              )}
            </>
          )}
        </Card>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Profissional"
        message="Tem certeza que deseja excluir este profissional? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />
    </div>
  );
};
